import axios from 'axios'
import { TokenManager, SecurityUtils, sessionManager } from '../utils/auth'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => {
    const parts = [];
    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        value.forEach((v) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
      } else if (value !== undefined && value !== null) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    return parts.join('&');
  },
})

// Request interceptor to add auth token and handle CSRF
api.interceptors.request.use(
  (config) => {
    const token = TokenManager.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add CSRF token if available
    const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken
    }

    // Add security headers
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    
    // Sanitize request data (but not FormData)
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = sanitizeRequestData(config.data)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh and security
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = TokenManager.getRefreshToken()
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/auth/refresh/`, {
            refresh: refreshToken,
          })

          const { access, expires_in } = response.data
          TokenManager.setTokens(access, refreshToken, expires_in)

          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        // Clear tokens and redirect to login
        TokenManager.clearTokens()
        sessionManager.clearTimers()
        
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    }

    return Promise.reject(error)
  }
)

// Utility function to sanitize request data
function sanitizeRequestData(data) {
  if (Array.isArray(data)) {
    return data.map(item => sanitizeRequestData(item))
  }
  
  if (data && typeof data === 'object') {
    const sanitized = {}
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') {
        sanitized[key] = SecurityUtils.sanitizeInput(value)
      } else if (typeof value === 'object') {
        sanitized[key] = sanitizeRequestData(value)
      } else {
        sanitized[key] = value
      }
    }
    return sanitized
  }
  
  return data
}

export const authService = {
  async login(email, password, rememberMe = false) {
    try {
      if (!SecurityUtils.isValidEmail(email)) {
        throw new Error('Please enter a valid email address')
      }
      
      const sanitizedEmail = SecurityUtils.sanitizeInput(email.toLowerCase().trim())
      
      const response = await api.post('/auth/login/', { 
        email: sanitizedEmail, 
        password 
      })
      
      const { access, refresh, expires_in } = response.data
      
      TokenManager.setTokens(access, refresh, expires_in, rememberMe)
      
      return response.data
    } catch (error) {
      console.error('Login error:', SecurityUtils.maskSensitiveData(error.response?.data || error.message))
      throw error
    }
  },

  async register(userData) {
    try {
      if (!SecurityUtils.isValidEmail(userData.email)) {
        throw new Error('Please enter a valid email address')
      }
      
      const passwordValidation = SecurityUtils.validatePassword(
        userData.password
      )
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors[0])
      }
      
      const sanitizedData = {
        email: SecurityUtils.sanitizeInput(
          userData.email.toLowerCase().trim()
        ),
        first_name: SecurityUtils.sanitizeInput(userData.firstName || ''),
        last_name: SecurityUtils.sanitizeInput(userData.lastName || ''),
        password: userData.password,
        password_confirm: userData.passwordConfirm || userData.password,
      }
      
      const response = await api.post('/auth/register/', sanitizedData)
      
      const { access, refresh, expires_in } = response.data
      
      if (access && refresh) {
        TokenManager.setTokens(access, refresh, expires_in)
      }
      
      return response.data
    } catch (error) {
      console.error(
        'Registration error:',
        SecurityUtils.maskSensitiveData(
          error.response?.data || error.message
        )
      )
      throw error
    }
  },

  async getCurrentUser() {
    try {
      const response = await api.get('/auth/profile/')
      return response.data
    } catch (error) {
      console.error('Get current user error:', error.response?.data || error.message)
      throw error
    }
  },

  async logout() {
    try {
      const refreshToken = TokenManager.getRefreshToken()
      if (refreshToken) {
        await api.post('/auth/logout/', { refresh: refreshToken })
      }
    } catch (error) {
      // Log error but don't throw - we still want to clear local tokens
      console.error('Logout error:', error)
    } finally {
      // Always clear tokens and session
      TokenManager.clearTokens()
      sessionManager.clearTimers()
    }
  },

  async requestPasswordReset(email) {
    const response = await api.post('/auth/password-reset/', { email })
    return response.data
  },

  async confirmPasswordReset(uid, token, newPassword, confirmPassword) {
    const response = await api.post(`/auth/password-reset-confirm/${uid}/${token}/`, {
      new_password: newPassword,
      new_password_confirm: confirmPassword,
    })
    return response.data
  },

  async updateProfile(profileData) {
    const response = await api.patch('/auth/profile/update/', profileData)
    return response.data
  },

  async refreshToken() {
    const refreshToken = TokenManager.getRefreshToken()
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh/`, {
        refresh: refreshToken,
      })
      
      const { access, expires_in } = response.data
      TokenManager.setTokens(access, refreshToken, expires_in)
      
      return response.data
    } catch (error) {
      // If refresh fails, clear tokens
      TokenManager.clearTokens()
      throw error
    }
  },

  // New security methods
  async changePassword(currentPassword, newPassword, confirmPassword) {
    try {
      // Validate new password
      const passwordValidation = SecurityUtils.validatePassword(newPassword)
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors[0])
      }
      
      if (newPassword !== confirmPassword) {
        throw new Error('New passwords do not match')
      }
      
      const response = await api.post('/auth/password-change/', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      })
      
      return response.data
    } catch (error) {
      console.error('Password change error:', SecurityUtils.maskSensitiveData(error.response?.data || error.message))
      throw error
    }
  },

  // Check if current session is valid
  async validateSession() {
    try {
      if (!TokenManager.isAuthenticated()) {
        return false
      }
      
      const response = await api.get('/auth/validate-session/')
      return response.status === 200
    } catch (error) {
      return false
    }
  },

  // Get current authentication status
  getAuthStatus() {
    return {
      isAuthenticated: TokenManager.isAuthenticated(),
      tokenExpiry: TokenManager.getTokenExpiry(),
      isExpired: TokenManager.isTokenExpired(),
    }
  },
}

export default api